import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  extractSectionWithMaxScoreFromArticles,
  RemovalMode,
} from "../common/commonDomain";

import { DecisionType } from "../../common/decisions";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  initArticleTextModels,
  LocalizedStringFieldModel,
} from "../../common/commonForms";

export type RemoveCompanyBoardFromArticlesOfAssociation = {
  type: DecisionType.removeCompanyBoardFromArticlesOfAssociation;
  paragraph?: number;
  removalMode?: RemovalMode;
};

export class RemoveCompanyBoardFromArticlesOfAssociationForm {
  readonly type = DecisionType.removeCompanyBoardFromArticlesOfAssociation;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext;
  readonly organizationnumber: string | undefined;
  readonly data?: RemoveCompanyBoardFromArticlesOfAssociation;
  paragraphNumber: ArticleSelectorModel;
  paragraphContainsMultipleClauses: BooleanFieldModel;
  paragraphTexts: LocalizedStringFieldModel[];

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    data?: RemoveCompanyBoardFromArticlesOfAssociation,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false, data: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.organizationnumber = context.companyInfo?.company.organizationnumber;
    this.data = data;
    this.paragraphNumber = new ArticleSelectorModel(context.inputState?.articles, data?.paragraph);
    this.paragraphTexts = initArticleTextModels({
      selectorModel: this.paragraphNumber,
      value: data?.removalMode?.type === "UpdateParagraph" ? data.removalMode.newTexts : undefined,
    });
    this.paragraphContainsMultipleClauses = new BooleanFieldModel({
      value: data?.removalMode?.type === "UpdateParagraph",
    });
  }

  init(): void {
    if (!this.paragraphNumber.selected) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        if (!this.paragraphNumber.selected) {
          this.paragraphNumber.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "BOARD"),
          );
        }
        break;
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.paragraphNumber.isValid &&
      (!this.paragraphContainsMultipleClauses.value ||
        this.paragraphTexts.every((t) => t.model.isValid))
    );
  }

  setParagraphNumber(paragraphNumber: number | undefined) {
    this.paragraphNumber.setSelected(paragraphNumber);
    this.paragraphTexts = initArticleTextModels({ selectorModel: this.paragraphNumber });
  }

  toggleParagraphContainsMultipleClauses() {
    this.paragraphContainsMultipleClauses.setValue(!this.paragraphContainsMultipleClauses.value);
  }

  validate(): boolean {
    return this.isValid;
  }
}
